export const soundcloudLogo =
  `${process.env.PUBLIC_URL || ''}/assets/soundcloud-logo-white.webp`;
export const deezerLogo =
  `${process.env.PUBLIC_URL || ''}/assets/deezer-logo.png`;

// Hoisted out of the per-row render loop - every row rebuilt this entire
// (deeply nested, with two inline callback closures) object from scratch on
// every render, for up to `displayCount` rows, even though only
// `plugins.tooltip.borderColor` actually varies per row.
export const ROW_CHART_OPTIONS_BASE = {
  responsive: true,
  // chart.js animates by default (~1s draw-in on mount, and again on any
  // data/options update) - with no animation config here, every sparkline
  // played that draw-in whenever a new row scrolled into view (displayCount
  // growing) and again whenever sorting changed. Disabled outright rather
  // than tuned, since a redraw on every keystroke/sort/scroll batch was
  // never meant to be a visible animation in the first place.
  animation: false,
  interaction: {
    intersect: false,
    mode: 'index'
  },
  plugins: {
    legend: { display: false },
    title: { display: false },
    tooltip: {
      backgroundColor: '#181c24',
      titleColor: '#fff',
      bodyColor: '#fff',
      borderWidth: 2,
      padding: 12,
      cornerRadius: 6,
      displayColors: false,
      callbacks: {
        title: function (tooltipItems) {
          const dateTime = tooltipItems[0].label;
          const match = dateTime.match(/(\d{4})_(\d{2})_(\d{2}) (\d{2}):(\d{2})/);
          if (match) {
            const [, year, month, day, hour, minute] = match;
            return `${month}/${day}/${year} ${hour}:${minute}`;
          }
          return dateTime;
        },
        label: function (context) {
          return `Popularity: ${context.parsed.y?.toLocaleString() || 'N/A'}`;
        }
      }
    },
    chartAreaBorder: {
      borderColor: '#fff',
      borderWidth: 2,
    }
  },
  elements: {
    point: {
      radius: 0,
      hoverRadius: 6,
      hitRadius: 15
    }
  },
  scales: {
    x: {
      grid: { display: false },
      title: { display: false },
      ticks: { display: false }
    },
    y: {
      grid: { display: false },
      title: { display: false },
      ticks: { display: false }
    }
  },
  layout: {
    padding: 0
  }
};

// Coerces a row's currentPopularity (a formatted string like "72,300", a
// raw number, or occasionally an unparseable/NaN value) into a number for
// sorting, substituting `worstValue` (+/-Infinity, chosen by sort direction)
// for anything that isn't a real number - including NaN, which unlike
// null/undefined isn't caught by `??` and would otherwise poison the
// subtraction (NaN - x is always NaN) and sort unpredictably instead of
// consistently landing at the bottom of the list.
export function popularityForSort(popularityNum, worstValue) {
  return typeof popularityNum === 'number' && !Number.isNaN(popularityNum) ? popularityNum : worstValue;
}

// bookmarkedAt is a JS Date immediately after a local save (not yet
// round-tripped through Firestore) but a Firestore Timestamp (with
// .seconds) once reloaded from a getDoc - normalize both to millis.
export function bookmarkedAtMillis(bookmark) {
  const at = bookmark?.bookmarkedAt;
  if (!at) return 0;
  if (typeof at.seconds === 'number') return at.seconds * 1000;
  if (typeof at.getTime === 'function') return at.getTime();
  return 0;
}

// Extract the artist SoundCloud profile URL from stored embed HTML
export function extractSoundcloudUrl(embedHtml) {
  if (!embedHtml) return null;
  // The public track page href, if the embed HTML includes the caption link.
  const hrefMatch = embedHtml.match(/href=["'](https?:\/\/soundcloud\.com\/[^"'?]+)["']/i);
  if (hrefMatch) return hrefMatch[1];
  // Fallback: the iframe src's url= param. Crucially this is usually an
  // api.soundcloud.com/tracks/<id> RESOURCE url, not a public page - opening
  // it unauthenticated returns a 401 JSON error, so only accept it if it's an
  // actual soundcloud.com public link.
  const srcMatch = embedHtml.match(/src=["']([^"']+)["']/i);
  if (!srcMatch) return null;
  const src = srcMatch[1].replace(/&amp;/g, '&');
  const urlParam = src.match(/[?&]url=([^&]+)/i);
  if (urlParam) {
    try {
      const decoded = decodeURIComponent(urlParam[1]);
      if (/^https?:\/\/soundcloud\.com\//i.test(decoded)) return decoded;
    } catch { /* fall through */ }
  }
  return null;
}

// A usable, public SoundCloud link for an artist row. Prefer the specific
// track permalink, then the artist's profile page, then whatever the embed
// yields - never an api.soundcloud.com resource url (which 401s when opened).
export function pickSoundcloudLink(trackUrl, profileUrl, embedHtml) {
  const isPublic = (u) => typeof u === 'string' && /^https?:\/\/soundcloud\.com\//i.test(u);
  if (isPublic(trackUrl)) return trackUrl;
  if (isPublic(profileUrl)) return profileUrl;
  return extractSoundcloudUrl(embedHtml);
}

// Parse artist name and key from a "Name (key)" heading.
export function parseArtistHeading(heading) {
  const lastOpenParen = heading.lastIndexOf(' (');
  const lastCloseParen = heading.lastIndexOf(')');

  if (lastOpenParen !== -1 && lastCloseParen !== -1 && lastCloseParen > lastOpenParen) {
    const name = heading.substring(0, lastOpenParen);
    const key = heading.substring(lastOpenParen + 2, lastCloseParen);
    return { name, key };
  }
  return { name: heading, key: "" };
}

// Not currently called anywhere (the actual per-row percent-change math in
// ArtistsTable's artistRows memo is inlined separately) - kept as-is from
// the pre-split file rather than deleted, since that's a behavior/dead-code
// call this refactor wasn't scoped to make.
export function calculatePercentChangeForTimeline(values, allDateTimes, timeline) {
  const validValues = values.filter(v => v !== null);
  if (validValues.length < 2) return { percentChange: "N/A", percentColor: "#fff", percentNum: null };

  const now = new Date();
  let cutoffDate;

  switch (timeline) {
    case "2D":
      cutoffDate = new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000);
      break;
    case "1W":
      cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case "1M":
      cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    case "1Y":
      cutoffDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
      break;
    case "ALL":
      cutoffDate = new Date(0);
      break;
    default:
      cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  }

  let startIndex = 0;
  if (timeline !== "ALL") {
    for (let i = allDateTimes.length - 1; i >= 0; i--) {
      const dateTime = allDateTimes[i];
      const date = new Date(dateTime);
      if (date >= cutoffDate) {
        startIndex = i;
      } else {
        break;
      }
    }
  }

  const timelineValues = [];
  for (let i = startIndex; i < values.length; i++) {
    if (values[i] !== null) {
      timelineValues.push(values[i]);
    }
  }

  if (timelineValues.length < 2) {
    return { percentChange: "N/A", percentColor: "#fff", percentNum: null };
  }

  const first = timelineValues[0];
  const last = timelineValues[timelineValues.length - 1];

  if (first === 0) {
    const percentChange = last === 0 ? "0%" : "∞";
    const percentNum = last === 0 ? 0 : null;
    const percentColor = last > 0 ? "#0f0" : last < 0 ? "#f00" : "#fff";
    return { percentChange, percentColor, percentNum };
  }

  const change = ((last - first) / Math.abs(first)) * 100;
  let formattedChange = change.toFixed(2);
  let percentChange, percentColor;

  if (change > 0) {
    percentChange = "+" + formattedChange + "%";
    percentColor = "#0f0";
  } else if (change < 0) {
    percentChange = formattedChange + "%";
    percentColor = "#f00";
  } else {
    percentChange = formattedChange + "%";
    percentColor = "#fff";
  }

  return { percentChange, percentColor, percentNum: change };
}

// Helper to get timeline display text
export function getTimelineDisplayText(timeline) {
  switch (timeline) {
    case "2D": return "2 days";
    case "1W": return "1 week";
    case "1M": return "1 month";
    case "1Y": return "1 year";
    case "ALL": return "all time";
    default: return "1 month";
  }
}

// Filters allDateTimes down to the ones within `timeline`, forward-filling
// the most recent historical point before the cutoff as a baseline so a
// sparse dataset can still calculate a 2D/1W percent change and draw a line
// starting from an accurate historical value.
export function getFilteredDataForTimeline(allDateTimes, timeline) {
  if (timeline === "ALL" || !allDateTimes || allDateTimes.length === 0) {
    return allDateTimes;
  }

  // The data might be historical, so "now" should be the most recent data point available, not the physical present day.
  let now = new Date();
  const latestTimestamp = allDateTimes[allDateTimes.length - 1];
  const latestMatch = latestTimestamp.match(/(\d{4})_(\d{2})_(\d{2}) (\d{2}):(\d{2})/);

  if (latestMatch) {
    const [, year, month, day, hour, minute] = latestMatch;
    now = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute));
  }

  let cutoffDate;

  switch (timeline) {
    case "2D":
      cutoffDate = new Date(now.getTime() - (2 * 24 * 60 * 60 * 1000)); // 2 days ago
      break;
    case "1W":
      cutoffDate = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000)); // 1 week ago
      break;
    case "1M":
      cutoffDate = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000)); // 1 month ago
      break;
    case "1Y":
      cutoffDate = new Date(now.getTime() - (365 * 24 * 60 * 60 * 1000)); // 1 year ago
      break;
    default:
      return allDateTimes;
  }

  let latestBeforeCutoffIndex = -1;
  const validIndices = [];

  allDateTimes.forEach((dateTime, index) => {
    // Parse 'YYYY_MM_DD HH:MM' format
    const match = dateTime.match(/(\d{4})_(\d{2})_(\d{2}) (\d{2}):(\d{2})/);
    if (!match) return;

    const [, year, month, day, hour, minute] = match;
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute));

    if (date >= cutoffDate) {
      validIndices.push(index);
    } else {
      latestBeforeCutoffIndex = index;
    }
  });

  if (latestBeforeCutoffIndex !== -1) {
    validIndices.unshift(latestBeforeCutoffIndex);
  }

  return validIndices.map(i => allDateTimes[i]);
}

// Helper to determine sign for filtering
export function getPercentSign(val) {
  if (val === "N/A" || val === "∞") return "other";
  if (val.endsWith("%")) {
    const num = parseFloat(val);
    if (num > 0) return "positive";
    if (num < 0) return "negative";
    if (num === 0) return "zero";
  }
  return "other";
}

// ── Location ─────────────────────────────────────────────────────────────────
// Artists outside the US have a city but no `state`, so the board showed
// "Melbourne, N/A". The second part falls back to the country (full name from
// `country`, or the Soundcharts `sc_country` code) so international artists
// read "Melbourne, Australia". Fields are checked in the order the table
// already used for each part.
const regionNames = (() => {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" });
  } catch {
    return null;
  }
})();

function countryName(profile) {
  if (profile.country) return profile.country;
  const code = profile.sc_country;
  if (!code) return "";
  try {
    return (regionNames && regionNames.of(String(code).toUpperCase())) || "";
  } catch {
    return "";
  }
}

// {town, region} for an artists_collection / artist-doc record; "N/A" when
// that part is unknown (what the callers already compared against).
export function resolveLocation(profile) {
  const p = profile || {};
  return {
    town: (p.hometown || p.home_town || p.city || "").trim() || "N/A",
    region: (p.state || p.home_state || "").trim() || countryName(p) || "N/A",
  };
}

// "Town, Region" with unknown parts dropped; "" when both are unknown.
export function formatLocation(town, region) {
  const known = (v) => v && v !== "N/A";
  return [town, region].filter(known).join(", ");
}
